import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PomodoroTimer } from '@/components/timer/PomodoroTimer';
import { useTimer } from '@/contexts/TimerContext';
import { useModals } from '@/contexts/ModalContext';
import { useApp } from '@/contexts/AppContext';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { toast } from 'sonner';
import { PenLine, Pencil, CheckCircle2, AlertTriangle, Clock, BookOpen, FileText, Tag } from 'lucide-react';

export function TimerModal() {
  const { timerOpen, timerProps, closeTimer, openRegistrar } = useModals();
  const { lastDuration, clearLastDuration, focusTime, status, setFocusTime } = useTimer();
  const { disciplinas, getConteudosByDisciplina, tiposEstudo, sessoes, addSessao } = useApp();
  const { podeCriarSessao, maxSessoesMes, planName, bloqueadas } = usePlanLimits();
  const disciplinasLiberadas = disciplinas.filter((d) => d.ativo !== false && !bloqueadas.has(d.id));

  const LS_KEY = 'pomodoro-last-context';
  const [disciplinaId, setDisciplinaId] = useState('');
  const [conteudoId, setConteudoId] = useState('');
  const [tipoEstudo, setTipoEstudo] = useState('estudo_teorico');
  const [showSummary, setShowSummary] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const conteudos = disciplinaId ? getConteudosByDisciplina(disciplinaId) : [];

  // Helper: derive last conteúdo for a discipline from sessions, validated against existing conteúdos
  const deriveLastConteudo = (discId: string): string | undefined => {
    const conteudosDisc = getConteudosByDisciplina(discId);
    const validIds = new Set(conteudosDisc.map((c) => c.id));
    const last = [...sessoes]
      .filter((s) => s.disciplinaId === discId && s.conteudoId && validIds.has(s.conteudoId))
      .sort((a, b) => new Date(b.data as any).getTime() - new Date(a.data as any).getTime())[0];
    return last?.conteudoId;
  };

  // Hidrata últimos valores ao abrir o modal
  useEffect(() => {
    if (!timerOpen) return;

    let savedDisc: string | undefined;
    let savedConteudo: string | undefined;
    let savedTipo: string | undefined;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as { disciplinaId?: string; conteudoId?: string; tipoEstudo?: string };
        savedDisc = saved.disciplinaId;
        savedConteudo = saved.conteudoId;
        savedTipo = saved.tipoEstudo;
      }
    } catch {
      /* ignore */
    }

    // Priorizar props vindas do contexto sobre localStorage
    const targetDiscRaw = timerProps?.disciplinaId ?? savedDisc;
    const targetDisc =
      targetDiscRaw && disciplinas.some((d) => d.id === targetDiscRaw) && !bloqueadas.has(targetDiscRaw) ? targetDiscRaw : '';

    if (targetDisc) {
      setDisciplinaId(targetDisc);
      const conteudosDisc = getConteudosByDisciplina(targetDisc);

      let targetConteudo: string | undefined;
      if (timerProps?.conteudoId && conteudosDisc.some((c) => c.id === timerProps.conteudoId)) {
        // Conteúdo explicitamente passado por props tem prioridade máxima
        targetConteudo = timerProps.conteudoId;
      } else {
        // Padrão: usar o último conteúdo estudado em sessão para esta disciplina,
        // com fallback para o salvo em localStorage caso não haja sessões.
        targetConteudo =
          deriveLastConteudo(targetDisc) ||
          (savedDisc === targetDisc && savedConteudo && conteudosDisc.some((c) => c.id === savedConteudo)
            ? savedConteudo
            : undefined);
      }
      if (targetConteudo) setConteudoId(targetConteudo);

      // Aplicar tempo do ciclo da disciplina quando veio por props e timer estiver ocioso
      if (timerProps?.disciplinaId && status === 'idle') {
        const disc = disciplinas.find((d) => d.id === targetDisc);
        if (disc?.tempoCicloMin && disc.tempoCicloMin !== focusTime) {
          setFocusTime(disc.tempoCicloMin);
        }
      }
    }

    const tipoCandidate = timerProps?.tipoEstudo ?? savedTipo;
    if (tipoCandidate && tiposEstudo.some((t) => t.id === tipoCandidate)) {
      setTipoEstudo(tipoCandidate);
    }

    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerOpen]);

  // Reset conteúdo apenas quando o usuário troca a disciplina manualmente (após hidratar)
  const prevDisciplinaRef = (useState({ value: '' })[0]);
  useEffect(() => {
    if (!hydrated) {
      prevDisciplinaRef.value = disciplinaId;
      return;
    }
    if (prevDisciplinaRef.value !== disciplinaId) {
      // Ao trocar manualmente, tentar derivar o último conteúdo da nova disciplina
      const derived = disciplinaId ? deriveLastConteudo(disciplinaId) : '';
      setConteudoId(derived || '');
      prevDisciplinaRef.value = disciplinaId;
    }
  }, [disciplinaId, hydrated, prevDisciplinaRef]);

  // Persiste seleções
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ disciplinaId, conteudoId, tipoEstudo }),
      );
    } catch {
      /* ignore */
    }
  }, [disciplinaId, conteudoId, tipoEstudo, hydrated]);

  useEffect(() => {
    if (!timerOpen) {
      setShowSummary(false);
      setHydrated(false);
    }
  }, [timerOpen]);

  // Abre o formulário completo (RegistrarSessaoModal) com dados pré-preenchidos
  const handleOpenFullForm = () => {
    const duration = lastDuration || (status === 'finished' ? focusTime : undefined);
    closeTimer();
    openRegistrar({
      tempoPreenchido: duration || undefined,
      disciplinaId: disciplinaId || undefined,
      conteudoId: conteudoId || undefined,
      tipoEstudo: tipoEstudo || undefined,
    });
    clearLastDuration();
  };

  // Salva a sessão diretamente sem abrir outro modal
  const handleQuickSave = async () => {
    const duration = lastDuration || (status === 'finished' ? focusTime : 0);

    // Sem disciplina/conteúdo ou sem duração: cair no formulário completo
    if (!disciplinaId || !conteudoId || !duration) {
      handleOpenFullForm();
      return;
    }

    if (bloqueadas.has(disciplinaId)) {
      toast.error(`Esta disciplina está bloqueada pelo plano ${planName}. Faça upgrade para registrá-la.`);
      return;
    }

    if (!podeCriarSessao) {
      toast.error(`Limite de ${maxSessoesMes} sessões/mês atingido no plano ${planName}. Faça upgrade para registrar mais.`);
      return;
    }

    try {
      await addSessao(
        {
          disciplinaId,
          conteudoId,
          tempoMin: duration,
          data: new Date(),
          tipoEstudo,
        },
        true,
      );
      toast.success('Sessão registrada com revisões agendadas!');
      clearLastDuration();
      closeTimer();
    } catch (err) {
      console.error('Erro ao registrar sessão pelo timer:', err);
      toast.error('Não foi possível registrar a sessão. Tente novamente.');
    }
  };

  const showRegister = lastDuration || status === 'finished';

  const disciplinaNome = disciplinas.find((d) => d.id === disciplinaId)?.nome;
  const conteudoNome = conteudos.find((c) => c.id === conteudoId)?.nome;
  const tipoNome = tiposEstudo.find((t) => t.id === tipoEstudo)?.nome ?? tipoEstudo;
  const tempoFinal = lastDuration || focusTime;
  const camposPendentes = !disciplinaId || !conteudoId;

  return (
    <Dialog open={timerOpen} onOpenChange={(open) => !open && closeTimer()}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Timer Pomodoro</DialogTitle>
          <DialogDescription className="text-center text-sm">Mantenha o foco com ciclos de estudo</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 items-center">
          {/* Left: Timer */}
          <div>
            <PomodoroTimer compact />
          </div>

          {/* Right: Context + Register OR Summary */}
          {showSummary && showRegister ? (
            <div className="space-y-4 animate-fade-in">
              <div className="text-center">
                <h3 className="text-lg font-semibold flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  Confirmar sessão
                </h3>
                <p className="text-muted-foreground text-xs mt-1">
                  Revise os dados antes de salvar
                </p>
              </div>

              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between pb-3 border-b">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Tempo
                  </span>
                  <span className="text-xl font-bold text-primary tabular-nums">
                    {tempoFinal} min
                  </span>
                </div>

                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm text-muted-foreground flex items-center gap-2 shrink-0">
                    <BookOpen className="h-4 w-4" /> Disciplina
                  </span>
                  <span className={`text-sm font-medium text-right ${disciplinaNome ? '' : 'text-muted-foreground italic'}`}>
                    {disciplinaNome ?? 'Não selecionada'}
                  </span>
                </div>

                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm text-muted-foreground flex items-center gap-2 shrink-0">
                    <FileText className="h-4 w-4" /> Conteúdo
                  </span>
                  <span className={`text-sm font-medium text-right ${conteudoNome ? '' : 'text-muted-foreground italic'}`}>
                    {conteudoNome ?? 'Não selecionado'}
                  </span>
                </div>

                <div className="flex items-start justify-between gap-3">
                  <span className="text-sm text-muted-foreground flex items-center gap-2 shrink-0">
                    <Tag className="h-4 w-4" /> Tipo de estudo
                  </span>
                  <span className="text-sm font-medium text-right capitalize">
                    {tipoNome?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              {camposPendentes && (
                <div className="flex items-start gap-2 rounded-md bg-destructive/10 text-destructive p-3 text-xs">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Você ainda pode salvar, mas recomendamos selecionar disciplina e conteúdo para um registro completo.
                  </span>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => setShowSummary(false)}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  className="flex-1"
                  onClick={handleQuickSave}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirmar e salvar
                </Button>
              </div>

              <button
                type="button"
                onClick={handleOpenFullForm}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 mx-auto block"
              >
                Adicionar questões, observações ou páginas
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Disciplina</Label>
                <Select value={disciplinaId} onValueChange={setDisciplinaId}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplinasLiberadas.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Conteúdo</Label>
                <Select value={conteudoId} onValueChange={setConteudoId} disabled={!disciplinaId}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={disciplinaId ? "Selecione..." : "Escolha disciplina"} />
                  </SelectTrigger>
                  <SelectContent>
                    {conteudos.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Tipo de Estudo</Label>
                <Select value={tipoEstudo} onValueChange={setTipoEstudo}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposEstudo.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {showRegister && (
                <div className="pt-2 animate-fade-in">
                  <Button
                    variant="default"
                    size="lg"
                    className="w-full"
                    onClick={() => setShowSummary(true)}
                  >
                    <PenLine className="h-5 w-5 mr-2" />
                    Revisar e salvar ({tempoFinal} min)
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
